import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Loader2, 
  ChevronRight,
  Target,
  Trophy,
  Play,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Hash
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClassifierExerciseModal = ({ isOpen, onClose }) => {
  const [difficulty, setDifficulty] = useState("easy");
  const [questionCount, setQuestionCount] = useState(5);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);
  
  const thaiNumbers = {
    1: "หนึ่ง", 2: "สอง", 3: "สาม", 4: "สี่", 5: "ห้า",
    6: "หก", 7: "เจ็ด", 8: "แปด", 9: "เก้า", 10: "สิบ"
  };

  const startExercise = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/classifier/exercise`, {
        difficulty,
        count: questionCount
      });
      setQuestions(response.data.questions);
      setUserAnswers([]);
      setCurrentQuestion(0);
      setSelectedAnswer(null);
      setShowResult(false);
      setResult(null);
      setGameStarted(true);
      toast.success("C'est parti !");
    } catch (err) {
      console.error("Error starting exercise:", err);
      toast.error("Erreur lors du chargement de l'exercice");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnswer = (answerIndex) => {
    setSelectedAnswer(answerIndex);
  };

  const nextQuestion = () => {
    const newAnswers = [...userAnswers, selectedAnswer];
    setUserAnswers(newAnswers);
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
    } else {
      evaluateExercise(newAnswers);
    }
  };

  const evaluateExercise = async (answers) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/classifier/evaluate`, {
        questions: questions,
        user_answers: answers
      });
      setResult(response.data);
      setShowResult(true);
    } catch (err) {
      console.error("Error evaluating:", err);
      toast.error("Erreur lors de l'évaluation");
    } finally {
      setIsLoading(false);
    }
  };

  const resetExercise = () => {
    setGameStarted(false);
    setQuestions([]);
    setUserAnswers([]);
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setResult(null);
  };

  const currentQ = questions[currentQuestion];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-emerald-50 to-teal-50">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-800">
            <Hash className="w-6 h-6" />
            Exercice Classificateurs (ลักษณนาม)
          </DialogTitle>
        </DialogHeader>

        {!gameStarted ? (
          /* Configuration Screen */
          <div className="space-y-6">
            <div className="bg-white/80 rounded-2xl p-6 border border-emerald-200">
              <h3 className="font-medium text-emerald-800 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5" />
                Configurer l'exercice
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Difficulté</label>
                  <div className="flex gap-2">
                    {[
                      { value: "easy", label: "Facile", desc: "3 options, nombres 1-3" },
                      { value: "medium", label: "Moyen", desc: "4 options, nombres 1-5" },
                      { value: "hard", label: "Difficile", desc: "4 options, nombres 2-10" }
                    ].map((d) => (
                      <button
                        key={d.value}
                        onClick={() => setDifficulty(d.value)}
                        className={`flex-1 px-4 py-3 rounded-xl border-2 transition-all ${
                          difficulty === d.value
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                            : 'border-slate-200 hover:border-emerald-300'
                        }`}
                      >
                        <div className="font-medium">{d.label}</div>
                        <div className="text-xs text-slate-500">{d.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Nombre de questions</label>
                  <div className="flex gap-2">
                    {[5, 10, 15, 20].map((n) => (
                      <button
                        key={n}
                        onClick={() => setQuestionCount(n)}
                        className={`px-6 py-2 rounded-xl border-2 font-medium transition-all ${
                          questionCount === n
                            ? 'border-emerald-500 bg-emerald-500 text-white'
                            : 'border-slate-200 hover:border-emerald-300'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/80 rounded-2xl p-6 border border-emerald-200">
              <h3 className="font-medium text-emerald-800 mb-3">Comment ça marche ?</h3>
              <div className="space-y-2 text-sm text-slate-600">
                <p>On te donne un mot thaï et un nombre</p>
                <p>Tu dois choisir le bon classificateur</p>
                <p>Exemple: Pour "3 oranges" → ส้ม + สาม + <span className="font-bold text-emerald-600">ลูก</span></p>
              </div>
            </div>

            <Button
              onClick={startExercise}
              disabled={isLoading}
              className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl text-lg"
              data-testid="start-classifier-exercise"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Play className="w-5 h-5 mr-2" />
              )}
              Commencer l'exercice
            </Button>
          </div>
        ) : showResult ? (
          /* Results Screen */
          <div className="space-y-6">
            <div className={`rounded-2xl p-6 text-center ${
              result.percentage >= 70 ? 'bg-green-100' : 
              result.percentage >= 50 ? 'bg-yellow-100' : 'bg-red-100'
            }`}>
              <div className={`text-5xl font-bold mb-2 ${
                result.percentage >= 70 ? 'text-green-600' : 
                result.percentage >= 50 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {result.score}/{result.total}
              </div>
              <div className="text-lg font-medium mb-2">
                {Math.round(result.percentage)}%
              </div>
              <p className="text-slate-700">{result.feedback}</p>
            </div>

            <div className="space-y-3 max-h-64 overflow-y-auto">
              {result.details.map((detail, idx) => (
                <div
                  key={idx}
                  className={`p-4 rounded-xl border-2 ${
                    detail.is_correct
                      ? 'bg-green-50 border-green-300'
                      : 'bg-red-50 border-red-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-thai text-lg">
                      {detail.word} ({detail.word_french}) × {detail.number}
                    </span>
                    {detail.is_correct ? (
                      <CheckCircle2 className="w-6 h-6 text-green-500" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-500" />
                    )}
                  </div>
                  <div className="text-sm">
                    <span className="text-slate-600">Réponse correcte: </span>
                    <span className="font-thai font-bold text-emerald-700">{detail.full_answer}</span>
                  </div>
                  {!detail.is_correct && (
                    <div className="text-sm text-red-600 mt-1">
                      Ta réponse: {detail.user_answer}
                    </div>
                  )}
                  <div className="text-xs text-slate-500 mt-1">{detail.explanation}</div>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={resetExercise}
                variant="outline"
                className="flex-1 rounded-xl"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Nouvel exercice
              </Button>
              <Button
                onClick={onClose}
                className="flex-1 rounded-xl bg-emerald-500 hover:bg-emerald-600"
              >
                Terminer
              </Button>
            </div>
          </div>
        ) : (
          /* Question Screen */
          <div className="space-y-6">
            {/* Progress */}
            <div className="flex items-center gap-4">
              <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all"
                  style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
                />
              </div>
              <span className="text-sm font-medium text-slate-600">
                {currentQuestion + 1}/{questions.length}
              </span>
            </div>

            {currentQ && (
              <>
                {/* Question */}
                <div className="bg-white rounded-2xl p-6 border-2 border-emerald-200 text-center">
                  <p className="text-slate-500 mb-2">Comment dit-on :</p>
                  <div className="text-3xl font-bold text-emerald-800 mb-2">
                    {currentQ.number} {currentQ.word_french}
                  </div>
                  <div className="font-thai text-2xl text-slate-700">
                    {currentQ.word} + {thaiNumbers[currentQ.number]} + <span className="text-emerald-500">?</span>
                  </div>
                </div>

                {/* Options */}
                <div className="grid grid-cols-2 gap-3">
                  {currentQ.options.map((option, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleAnswer(idx)}
                      className={`p-4 rounded-xl border-2 font-thai text-xl transition-all ${
                        selectedAnswer === idx
                          ? 'border-emerald-500 bg-emerald-100 scale-105'
                          : 'border-slate-200 hover:border-emerald-300 hover:bg-emerald-50'
                      }`}
                      data-testid={`classifier-option-${idx}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>

                {/* Next Button */}
                <Button
                  onClick={nextQuestion}
                  disabled={selectedAnswer === null || isLoading}
                  className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl text-lg disabled:opacity-50"
                  data-testid="next-question-btn"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : currentQuestion < questions.length - 1 ? (
                    <>
                      Suivant
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </>
                  ) : (
                    <>
                      Voir les résultats
                      <Trophy className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClassifierExerciseModal;
